import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import { annotationsReducer } from '../reducers';
import {
  annotation as annotationHelpers,
  utils
} from '../helpers';
import { flatten, mapValues, orderBy, values, keys } from 'lodash';

const annotationsByTypedIdOrderByDate = createCachedSelector(
  annotationsReducer.annotationsByBuildIds,
  annotationsReducer.annotationsByScenarioIds,
  annotationsReducer.annotationsByAppIds,
  (_state, type) => type,
  (byBuildId, byScenarioId, byAppId, type) => {
    const typeId = annotationHelpers.getTypedIdName(type);
    if (!typeId) return [];

    // determines which 'byType' to use, will only reveal 1
    const relevantTypedAnnotations = [byBuildId, byScenarioId, byAppId]
      .map(a => values(a))
      .filter(a => {
        // a is still an array of arrays, just need to check 1 object contained
        if (a.length === 0) return false;
        return a[0].some(a => a[typeId]);
      });
    return flatten(relevantTypedAnnotations).reduce((result, annotations) => {
      result[annotations[0][typeId]] =
        orderBy(annotations, a => a.created_at, 'desc');
      return result;
    }, {});
  }
)(
  (_state, type) => type
);

export const getAnnotationsByScenarioIdsOrderByDate = (state) =>
  annotationsByTypedIdOrderByDate(state, annotationHelpers.SCENARIO_TYPED);

export const getAnnotationsByBuildIdsOrderByDate = (state) =>
  annotationsByTypedIdOrderByDate(state, annotationHelpers.BUILD_TYPED);

export const getAnnotationsByAppIdsOrderByDate = (state) =>
  annotationsByTypedIdOrderByDate(state, annotationHelpers.APP_TYPED);

export const getAnnotationsByExtraInfoByTypedId = createCachedSelector(
  (state, _prop, type) => annotationsByTypedIdOrderByDate(state, type),
  (_state, prop) => prop,
  (annotationsByTypedId, extraInfoProp) => {
    const result = mapValues(
      annotationsByTypedId,
      (annotations) => utils.propertyAccumulatorReducerByFunc(
        annotations,
        (a) => a.extra_info && a.extra_info[extraInfoProp]
      )
    );
    return result;
  }
)(
  (_state, prop, type) => prop + type
);

export const getFeedbackScoresByBuildIds = createSelector(
  getAnnotationsByBuildIdsOrderByDate,
  (annotationsByBuildIds) => {
    const results = {};
    for (const build in annotationsByBuildIds) {
      results[build] = annotationsByBuildIds[build].reduce((result, a) => {
        if (a.status === annotationHelpers.
          ALL_ANNOTATIONS_STATUSES.UP_VOTE)
        {
          result.voteTotal++;
          result.upVotes++;
        }
        else
        {
          result.voteTotal--;
          result.downVotes++;
        }
        return result;
      }, { upVotes: 0, downVotes: 0, voteTotal: 0 });
    }
    return results;
  }
);

export const getUserVoteByBuildIDs = createSelector(
  getAnnotationsByBuildIdsOrderByDate,
  (annotationsByBuildIds) => {
    const results = {};
    const username = localStorage.getItem('userName') || '';
    for (const build in annotationsByBuildIds) {
      results[build] = { id: 0, status: 'None' };
      annotationsByBuildIds[build].forEach(a => {
        if (a.user === username) {
          results[build] = a;
        }
      });
    }
    return results;
  }
);


export const getFeedbackScoresByDispatcherIdsByBuildIds = createSelector(
  (state) => getAnnotationsByExtraInfoByTypedId(
    state,
    annotationHelpers.EXTRA_INFO_PROPS.DISPATCHER_ID,
    annotationHelpers.BUILD_TYPED
  ),
  (annotationsByDispatcherIdsByBuildIds) => {
    const results = {};
    for (const buildId in annotationsByDispatcherIdsByBuildIds) {
      const annotationsByDispatcherIds =
      annotationsByDispatcherIdsByBuildIds[buildId];
      const feedbackByDispatcherIds = keys(annotationsByDispatcherIds).
        reduce((resultByDispatcherId, dispatcherId) => {
          const annotations = annotationsByDispatcherIds[dispatcherId];
          const voteResult = { upVotes: 0, downVotes: 0, voteTotal: 0 };
          annotations.forEach( a => {
            if (a.status === annotationHelpers.
              ALL_ANNOTATIONS_STATUSES.UP_VOTE)
            {
              voteResult.voteTotal++;
              voteResult.upVotes++;
            }
            else
            {
              voteResult.voteTotal--;
              voteResult.downVotes++;
            }
          });
          resultByDispatcherId[dispatcherId] = voteResult;
          return resultByDispatcherId;
        }, {});
      results[buildId] = feedbackByDispatcherIds;
    }
    return results;
  });


// WEBPACK FOOTER //
// src/selectors/annotation.js